export type ApiFilterOption = 'raw' | 'filtered';
export type DisplayFilterOption = 'raw' | 'psg' | 'hst';
export type FilterType = 'none' | 'auto';

export interface SignalFileItem {
  label: string;
  key: string;
  signedUrl: string;
}

export interface FileListData {
  filtered: SignalFileItem[];
  raw?: SignalFileItem[]; // optional if not always present
}

export interface FileListResponse {
  message: string;
  data: FileListData;
}

export enum ReportTemplate {
  HST_TYPE_2 = 'HST type 2',
  EEG_SLEEP_WAKE = 'EEG',
  PSG = 'PSG',
  // Variant of PSG that renders identically (aliased to PSG for rendering and
  // the set of available sections via getEffectiveTemplate) and whose report
  // data is served by the backend from PSG, but which is fetched and saved
  // under its own report type so it can carry its own default sections and a
  // distinct header ("Sleep Study Report - Polysomnography").
  PSG_STANDARD = 'PSG Standard',
  MSLT = 'MSLT',
  MULTI_NIGHT_EEG = 'Multi Night EEG',
  MULTI_NIGHT_HST = 'Multi Night HST type 2',
  MULTI_NIGHT_PSG = 'Multi Night PSG',
  // Titration. Lower case because this is the report type the scoring
  // algorithm publishes its output under, and the study type recorded on the
  // test is the same string.
  TITRATION = 'titration',
  // A titration that opens with an untreated baseline. Lower case for the same
  // reason as titration: the algorithm publishes its report data under it.
  SPLIT_NIGHT = 'split_night',
}

export type ReportTemplateType =
  (typeof ReportTemplate)[keyof typeof ReportTemplate];

/**
 * Study types and report templates that are a titration. A split night is a
 * titration whose first part runs without treatment, so it is booked with a
 * titration type and its report carries everything the titration report does.
 */
export const TITRATION_TEMPLATES: readonly string[] = [
  ReportTemplate.TITRATION,
  ReportTemplate.SPLIT_NIGHT,
];

export const isTitrationTemplate = (value?: string | null): boolean =>
  !!value && TITRATION_TEMPLATES.includes(value);

export enum TitrationType {
  CPAP = 'CPAP',
  BIPAP = 'BiPAP',
  ASV = 'ASV',
  ST = 'S/T',
}

export const TITRATION_TYPE_OPTIONS = Object.values(TitrationType).map(
  (value) => ({ viewValue: value, value }),
);
